import type { CallScriptHistoryOf } from "./callScriptShared.types";

// ---------------------------------------------------------------------------
// callScriptResidence.types
//
// Field model + history shapes for the residence ("Customer Verification")
// Call Script. Mirrors callScript.types.ts, which does the same job for the
// employment script.
//
// Served by WorkbenchResidenceVerificationApi (getResidenceHistory /
// saveResidenceCallScript), both typed `void` on the generated client; the
// history entry follows recap's CallScriptResidenceHistoryRow.
// ---------------------------------------------------------------------------

/** Residence-verification fields captured during a Customer Verification call. */
export interface ResidenceVerifiedFields {
  // Contact block
  phoneNumberDialed: string;
  wasOriginalNumberBad: boolean;
  dateOfBirth: string;
  primaryNumber: string;
  secondaryNumber: string;
  signedCopyProvided: string;

  // Physical address
  physicalStreetNumber: string;
  physicalStreetName: string;
  physicalApartmentNumber: string;
  physicalZip: string;
  physicalCity: string;
  physicalState: string;

  // Mailing address
  mailingStreetNumber: string;
  mailingStreetName: string;
  mailingApartmentNumber: string;
  mailingZip: string;
  mailingCity: string;
  mailingState: string;

  // Residence type + landlord, note-only (no verification question)
  residenceOwnerType: string;
  landlordFirstName: string;
  landlordLastName: string;
  landlordCompanyName: string;
  landlordPhone: string;
  landlordStreetNumber: string;
  landlordStreetName: string;
  landlordApartmentNumber: string;
  landlordZip: string;
  landlordCity: string;
  landlordState: string;

  // Deal / down payment block
  whoIsPrimaryDriver: string;
  tradeIn: string;
  grossTradeInAmount: number | "";
  tradeInPayoffAmount: number | "";
  cashDownPaidOnDelivery: number | "";
  amountDueAfterDelivery: number | "";
  whenIsFinalAmountDue: string;
}

export type ResidenceFieldKey = keyof ResidenceVerifiedFields;

/** One row of the Vehicle Accessories table (CAPS2 `customerVerificationVehicleAddonWOList`). */
export interface ResidenceCallScriptAccessory {
  id: string;
  description: string;
  price?: number;
}

/** Blank "Verified Information" — what the analyst captures live on the call, never a copy of saved data. */
export const EMPTY_RESIDENCE_VERIFIED_FIELDS: ResidenceVerifiedFields = {
  phoneNumberDialed: "",
  wasOriginalNumberBad: false,
  dateOfBirth: "",
  primaryNumber: "",
  secondaryNumber: "",
  signedCopyProvided: "",
  physicalStreetNumber: "",
  physicalStreetName: "",
  physicalApartmentNumber: "",
  physicalZip: "",
  physicalCity: "",
  physicalState: "",
  mailingStreetNumber: "",
  mailingStreetName: "",
  mailingApartmentNumber: "",
  mailingZip: "",
  mailingCity: "",
  mailingState: "",
  residenceOwnerType: "",
  landlordFirstName: "",
  landlordLastName: "",
  landlordCompanyName: "",
  landlordPhone: "",
  landlordStreetNumber: "",
  landlordStreetName: "",
  landlordApartmentNumber: "",
  landlordZip: "",
  landlordCity: "",
  landlordState: "",
  whoIsPrimaryDriver: "",
  tradeIn: "",
  grossTradeInAmount: "",
  tradeInPayoffAmount: "",
  cashDownPaidOnDelivery: "",
  amountDueAfterDelivery: "",
  whenIsFinalAmountDue: "",
};

/**
 * The CAPS2 verification-question text for each expirable field, taken from
 * `showAndHideTheFields()`'s expired-question matching. Matched
 * (case-insensitively) against the `getResidenceCallScriptQuestions`
 * catalog to resolve each field's `verificationQuestionPk` — see
 * buildResidenceQuestionPkMap.
 *
 * The five unconditional down-payment amounts and the residence-type/landlord
 * rows have no question counterpart and so are absent here; they reach recap
 * only through the note text.
 */
export const RESIDENCE_CALL_SCRIPT_QUESTION_TEXT = {
  phoneNumberDialed: "Phone Number Dialed",
  wasOriginalNumberBad: "Was original number bad?",
  dateOfBirth: "Date of Birth(Required for RPC)",
  primaryNumber: "Primary Number",
  secondaryNumber: "Secondary Number",
  signedCopyProvided: "Was a Signed copy of the contract provided by the Dealer?",
  physicalStreetNumber: "Physical Address Street No",
  physicalStreetName: "Physical Address Street Name",
  physicalApartmentNumber: "Physical Address Apt",
  physicalZip: "Physical Address Zip",
  physicalCity: "Physical Address City",
  physicalState: "Physical Address State",
  mailingStreetNumber: "Mailing Address Street No",
  mailingStreetName: "Mailing Address Street Name",
  mailingApartmentNumber: "Mailing Address Apt",
  mailingZip: "Mailing Address Zip",
  mailingCity: "Mailing Address City",
  mailingState: "Mailing Address State",
  whoIsPrimaryDriver: "Who is the Primary Driver?",
  tradeIn: "Trade-in(Y/N)",
} as const satisfies Partial<Record<ResidenceFieldKey, string>>;

/** Fields that carry a CAPS2 verification question (and so appear in `verificationAnswers[]`). */
export type ResidenceQuestionFieldKey = keyof typeof RESIDENCE_CALL_SCRIPT_QUESTION_TEXT;

/** One per-question verified-vs-saved comparison on a history entry. */
export interface ResidenceCallScriptHistoryAnswer {
  verificationAnswerPk: number;
  verificationQuestionPk?: number;
  questionText: string;
  answerEntered: string;
  answerStored: string;
  isAnswerMatch: "Y" | "N";
}

/** A saved Customer Verification call. */
export interface ResidenceCallScriptEntry {
  verificationPhoneCallPk: number;
  /** Empty when the response omits it. */
  verificationOutcome: string;
  phoneNumberDialed: string;
  isPhoneNoBad: "Y" | "N";
  isVerificationOutsourced: "Y" | "N";
  /** ISO date verified on the call; empty when none. */
  dob: string;
  /** ISO datetime the call occurred. */
  occurredAt: string;
  analystName: string;
  applicantAddressPk?: number;
  answers: ResidenceCallScriptHistoryAnswer[];
}

export type ResidenceCallScriptHistory = CallScriptHistoryOf<ResidenceCallScriptEntry>;
